import React, { useState, useCallback, useEffect, useRef, CSSProperties } from "react";
import { FaTrash, FaEdit, FaCheck, FaPlus } from "react-icons/fa";
import { TipoFormulario } from "../../services/constantes";
import Select, { components as ReactSelectComponents } from "react-select";
import styled from "styled-components";
import Modal from "react-bootstrap/Modal";
import { Campo } from "../../services/tipos";
import DecimalMonetario from "../decimal";

interface Column {
  label: string;
  value: string;
  editable?: boolean;
  modalField?: Campo;
  renderCell?: (value: any, row: any) => React.ReactNode;
  onlyModal?: boolean; // Nova propriedade
}

interface GridColumn {
  label: string;
  field: string;
  defaultValue?: string;
  renderCell?: (value: any, row: any) => React.ReactNode;
}

interface GroupedOption {
  label: string;
  options: any[];
}

export interface SelectGridProps {
  columns: Column[];
  dataSource: any[];
  initialGridData?: any[];
  onDataSourceChange: (data: any[]) => void;
  optionLabelField: string;
  onAcaoExtra?: () => void;
  style?: React.CSSProperties;
  height?: string | number;
  modo: TipoFormulario;
  tituloBotaoExtra?: string;
  iconeBotaoExtra?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  criticarRepeticao?: boolean;
  useGridOptions?: boolean;
  gridColumns?: GridColumn[];
  setData?: any[];
  forceUpdate?: boolean;
  orderByField?: string;
  maxRecords?: number;
  groupByField?: string;
  groupStyles?: CSSProperties;
  groupBadgeStyles?: CSSProperties;
  formatGroupLabel?: (data: GroupedOption) => React.ReactNode;
  modalTitle?: string;
  modalSaveButtonText?: string;
  modalCancelButtonText?: string;
  diffField?: string;
}

const formatarParaReais = (valor: number | string) => {
  if (!valor) return "R$ 0,00";
  const num = typeof valor === 'string' ? parseFloat(valor) : valor;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(num);
};

const SelectGridContainer = styled.div<{ height: string | number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  border: 1px solid #e0e3e8;
  border-radius: 8px;
  padding: 16px;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  height: ${({ height }) => (typeof height === 'number' ? `${height}px` : height)};
`;

const SelectGridHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SelectGridSelect = styled(Select)`
  flex: 1;
  font-size: 14px;
  
  .react-select__control {
    min-height: 44px;
    border: 1px solid #d0d5dd;
    border-radius: 8px;
    box-shadow: none;
    transition: all 0.2s;
    background-color: #f9fafb;
    
    &:hover {
      border-color: #9aa4b2;
      background-color: #fff;
    }
    
    &:focus-within {
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
      background-color: #fff;
    }
  }
  
  .react-select__value-container {
    padding: 8px 12px;
  }
  
  .react-select__input {
    color: #1f2937;
  }
  
  .react-select__placeholder {
    color: #6b7280;
  }
  
  .react-select__indicator {
    padding: 8px;
    color: #6b7280;
    
    &:hover {
      color: #4b5563;
    }
  }
  
  .react-select__menu {
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    margin-top: 4px;
    z-index: 9999 !important;
  }
  
  .react-select__menu-list {
    padding: 8px;
    z-index: 9999 !important;
  }
  
  .react-select__option {
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.2s;
    
    &--is-focused {
      background-color: #f3f4f6;
    }
    
    &--is-selected {
      background-color: #3b82f6;
      color: white;
    }
  }
  
  .react-select__group-heading {
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #4b5563;
    letter-spacing: 0.5px;
  }
`;

const GridOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
  padding: 12px;
  max-height: 400px;
  overflow-y: auto;
`;

const GridOptionItem = styled.div<{ $included: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid ${({ $included }) => ($included ? '#d1fae5' : '#e5e7eb')};
  border-radius: 8px;
  cursor: ${({ $included }) => ($included ? 'not-allowed' : 'pointer')};
  background-color: ${({ $included }) => ($included ? '#f0fdf4' : '#fff')};
  transition: all 0.2s;
  position: relative;
`;

const GridOptionCell = styled.div`
  font-size: 13px;
  margin-bottom: 8px;
  color: #374151;
  display: flex;
  align-items: flex-start;
  
  strong {
    font-weight: 600;
    color: #111827;
    min-width: 100px;
    display: inline-block;
  }
  
  span {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
  }
`;

const IncludedTag = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #10b981;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HighlightedText = styled.span`
  background-color: #fef08a;
  font-weight: 500;
  padding: 0 2px;
  border-radius: 2px;
`;

const SelectGridButtons = styled.div`
  display: flex;
  gap: 8px;
  position: relative;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SelectGridButton = styled.button<{ disabled?: boolean; variant?: 'remove' | 'default' | 'add' }>`
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-size: 14px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: all 0.2s;
  
  ${({ variant }) =>
    variant === 'remove' ? `
      color: #ffffff;
      background-color: #ef4444;
      border-color: #ef4444;
      
      &:hover:not(:disabled) {
        background-color: #dc2626;
        border-color: #dc2626;
      }
    ` :
      variant === 'add' ? `
      color: #ffffff;
      background-color: #10b981;
      border-color: #10b981;
      
      &:hover:not(:disabled) {
        background-color: #059669;
        border-color: #059669;
      }
    ` : `
      &:hover:not(:disabled) {
        background-color: #f8fafc;
        border-color: #d1d5db;
      }
    `}
`;

const SelectGridTableContainer = styled.div`
  flex: 1;
  border: 1px solid #e0e3e8;
  border-radius: 8px;
  background-color: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const SelectGridTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHeader = styled.th`
  padding: 12px 16px;
  text-align: left;
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
  position: sticky;
  top: 0;
  z-index: 2;
  border-bottom: 1px solid #e5e7eb;
  
  &:first-child {
    border-top-left-radius: 8px;
    width: 50px;
    text-align: center;
  }
  
  &:last-child {
    border-top-right-radius: 8px;
    width: 120px;
    text-align: center;
    position: sticky;
    right: 0;
    background-color: #f9fafb;
  }
`;

const TableHeaderRow = styled.tr`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #f9fafb;
`;

const CheckboxCell = styled.td`
  padding: 12px;
  text-align: center;
  vertical-align: middle;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
  position: relative;
  
  &:hover > .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
`;

const TooltipText = styled.span`
  visibility: hidden;
  width: auto;
  background-color: #fff;
  color: #111827;
  text-align: center;
  border-radius: 4px;
  padding: 8px;
  position: absolute;
  z-index: 10;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  white-space: normal;
  max-width: 300px;
  font-size: 13px;
`;

const ActionCell = styled.td`
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
  text-align: center;
  position: sticky;
  right: 0;
  background-color: white;
  z-index: 1;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  border-radius: 6px;
  transition: all 0.2s;
  
  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #ef4444;
  background-color: rgba(239, 68, 68, 0.1);
  
  &:hover:not(:disabled) {
    color: #dc2626;
    background-color: rgba(239, 68, 68, 0.2);
  }
`;

const EditButton = styled(ActionButton)`
  color: #3b82f6;
  background-color: rgba(59, 130, 246, 0.1);
  
  &:hover:not(:disabled) {
    color: #2563eb;
    background-color: rgba(59, 130, 246, 0.2);
  }
`;

const NoRecordsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 200px;
  padding: 40px;
`;

const NoRecords = styled.div`
  font-size: 15px;
  color: #6b7280;
  text-align: center;
`;

const ModalOverlay = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  display: ${({ $show }) => ($show ? 'block' : 'none')};
`;

const CenteredModal = styled(Modal)`
  display: flex !important;
  align-items: center;
  justify-content: center;
  
  .modal-dialog {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
  }
  
  .modal-content {
    border: none;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
  
  .modal-header {
    border-bottom: 1px solid #e5e7eb;
    padding: 20px 24px;
    
    .modal-title {
      font-weight: 600;
      color: #111827;
    }
  }
  
  .modal-body {
    padding: 24px;
  }
  
  .modal-footer {
    border-top: 1px solid #e5e7eb;
    padding: 16px 24px;
    justify-content: flex-end;
    gap: 12px;
    
    .btn {
      padding: 8px 16px;
      border-radius: 6px;
      font-weight: 500;
      transition: all 0.2s;
    }
    
    .btn-secondary {
      background-color: #f3f4f6;
      border-color: #f3f4f6;
      color: #374151;
      
      &:hover {
        background-color: #e5e7eb;
        border-color: #e5e7eb;
      }
    }
    
    .btn-primary {
      background-color: #3b82f6;
      border-color: #3b82f6;
      
      &:hover {
        background-color: #2563eb;
        border-color: #2563eb;
      }
    }
  }
`;

const ModalRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  gap: 16px;
`;

const ModalCol = styled.div<{ $width: number }>`
  flex: 0 0 calc(${({ $width }) => ($width / 12) * 100}% - 16px);
  max-width: calc(${({ $width }) => ($width / 12) * 100}% - 16px);
  box-sizing: border-box;
  
  .form-label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #374151;
    font-size: 14px;
  }
  
  .form-control {
    width: 100%;
    padding: 10px 12px;
    font-size: 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    transition: all 0.2s;
    background-color: #fff;
    
    &:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
      outline: none;
    }
    
    &.is-invalid {
      border-color: #ef4444;
    }
  }
  
  .invalid-feedback {
    color: #ef4444;
    font-size: 13px;
    margin-top: 4px;
  }
`;

const MoneyValue = styled.span`
  font-family: 'Roboto Mono', monospace;
  font-weight: 500;
  color: #065f46;
  background-color: #d1fae5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
`;

const highlightText = (text: string, query: string) => {
  if (!query || !text) return text;
  const regex = new RegExp(`(${query})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, index) =>
    regex.test(part) ? <HighlightedText key={index}>{part}</HighlightedText> : part
  );
};

const filterOptionsByGridColumns = (options: any[], gridColumns: GridColumn[], query: string) => {
  if (!query) {
    return options;
  }

  const lowerQuery = query.toLowerCase();
  const filtered = options.filter((option) => {
    const matches = gridColumns.some((col) => {
      const value = option.value[col.field]?.toString().toLowerCase() || '';
      return value.includes(lowerQuery);
    });
    return matches;
  });

  return filtered;
};

const defaultFormatGroupLabel = (data: GroupedOption) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    margin: '4px 0',
  }}>
    <span style={{ fontWeight: 600, fontSize: '13px', color: '#4b5563' }}>{data.label}</span>
    <span style={{
      backgroundColor: '#e5e7eb',
      borderRadius: '12px',
      color: '#4b5563',
      display: 'inline-block',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '1',
      padding: '4px 8px',
    }}>{data.options.length}</span>
  </div>
);

const SelectGrid: React.FC<SelectGridProps> = ({
  columns,
  dataSource,
  initialGridData = [],
  onDataSourceChange,
  optionLabelField,
  onAcaoExtra,
  style,
  height = "auto",
  modo,
  tituloBotaoExtra,
  iconeBotaoExtra,
  criticarRepeticao = false,
  useGridOptions = false,
  gridColumns = [],
  setData,
  forceUpdate = false,
  orderByField,
  maxRecords,
  groupByField,
  groupStyles,
  groupBadgeStyles,
  formatGroupLabel,
  modalTitle = "Editar Campos",
  modalSaveButtonText = "Salvar",
  modalCancelButtonText = "Cancelar",
  diffField,
}) => {
  const [selectedOption, setSelectedOption] = useState<any | null>(null);
  const [gridData, setGridData] = useState<any[]>(initialGridData);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [tableKey, setTableKey] = useState(0);
  const [searchInput, setSearchInput] = useState<string>("");
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Filtra as colunas que devem aparecer na grid (onlyModal !== true)
  const gridVisibleColumns = columns.filter(col => !col.onlyModal);

  const sortData = (data: any[], field?: string) => {
    if (!field) return data;
    return [...data].sort((a, b) => {
      const valueA = a[field]?.toString().toLowerCase() || "";
      const valueB = b[field]?.toString().toLowerCase() || "";
      return valueA.localeCompare(valueB);
    });
  };

  const isItemEqual = useCallback((item1: any, item2: any) => {
    if (diffField) {
      return item1.id === item2.id &&
        String(item1[diffField]) === String(item2[diffField]);
    }
    return item1.id === item2.id;
  }, [diffField]);

  const addItemToGrid = useCallback((item: any) => {
    if (maxRecords !== undefined && gridData.length >= maxRecords) {
      return;
    }

    const itemExists = gridData.some(gridItem => isItemEqual(gridItem, item));

    if (criticarRepeticao && itemExists) {
      return;
    }

    const newItem = {
      ...item,
      selected: false,
      id: item.id
    };

    const updatedGridData = [...gridData, newItem];
    const sortedGridData = sortData(updatedGridData, orderByField);
    setGridData(sortedGridData);
    onDataSourceChange(sortedGridData);
    setTableKey((prev) => prev + 1);
    setSelectedOption(null);

    setTimeout(() => {
      if (tableContainerRef.current) {
        const container = tableContainerRef.current;
        const lastRow = container.querySelector('tbody tr:last-child');
        if (lastRow) {
          lastRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 100);
  }, [gridData, maxRecords, criticarRepeticao, onDataSourceChange, orderByField, isItemEqual]);

  const EditModal = ({ show, onHide, item, onSave, campos }: {
    show: boolean;
    onHide: () => void;
    item: any;
    onSave: (data: any) => void;
    campos: Campo[];
  }) => {
    const [localItem, setLocalItem] = useState(item);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
      setLocalItem(item);
    }, [item]);

    const handleChange = (field: string, value: any) => {
      setLocalItem((prev: any) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      const newErrors: Record<string, string> = {};
      campos.forEach(campo => {
        if (campo.required && !localItem[campo.nome]) {
          newErrors[campo.nome] = `"${campo.label}" é obrigatório`;
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      onSave(localItem);
    };

    const camposPorLinha: Record<number, Campo[]> = {};
    campos.forEach(campo => {
      if (!camposPorLinha[campo.linha]) {
        camposPorLinha[campo.linha] = [];
      }
      camposPorLinha[campo.linha].push(campo);
    });

    return (
      <>
        <CenteredModal show={show} onHide={() => {
          onHide();
          setSelectedOption(null);
        }} size="lg" centered>
          <Modal.Header>
            <Modal.Title>{modalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleSubmit}>
              {Object.entries(camposPorLinha).map(([linha, camposDaLinha]) => (
                <ModalRow key={`linha-${linha}`}>
                  {camposDaLinha.map(campo => (
                    <ModalCol key={campo.nome} $width={campo.largura} data-tour={`selectgrid-campo-${campo.nome}`}>
                      <div className="mb-1">
                        <label className="form-label">
                          {campo.label}
                          {campo.required && <span className="text-danger"> *</span>}
                        </label>

                        {campo.tipo === 'money' ? (
                          <DecimalMonetario
                            getFieldValue={() => localItem[campo.nome] || ''}
                            handleInputChange={(field: any, value: any) => handleChange(field, value)}
                            campo={{
                              nome: campo.nome,
                              tipo: "monetario",
                              required: campo.required,
                              modo: modo
                            }}
                            apenasLeitura={campo.apenasLeitura}
                          />
                        ) : campo.tipo === 'number' ? (
                          <input
                            type="number"
                            step="any"
                            className={`form-control ${errors[campo.nome] ? 'is-invalid' : ''}`}
                            value={localItem[campo.nome] || ''}
                            disabled={campo.apenasLeitura}
                            min="0"
                            onChange={(e) => {
                              const value = e.target.value;
                              // Permite números, ponto e vírgula
                              if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
                                handleChange(campo.nome, value);
                              }
                            }}
                          />
                        ) : (
                          <input
                            type="text"
                            className={`form-control ${errors[campo.nome] ? 'is-invalid' : ''}`}
                            value={localItem[campo.nome] || ''}
                            onChange={(e) => handleChange(campo.nome, e.target.value)}
                            disabled={campo.apenasLeitura}
                          />
                        )}

                        {errors[campo.nome] && (
                          <div className="invalid-feedback">{errors[campo.nome]}</div>
                        )}
                      </div>
                    </ModalCol>
                  ))}
                </ModalRow>
              ))}
            </form>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={onHide}>
              {modalCancelButtonText}
            </button>
            {modo !== TipoFormulario.tpVisualizacao && (
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#1F467E', borderColor: '#1F467E' }}
                onClick={handleSubmit}
                data-tour="selectgrid-btn-salvar"
              >
                {modalSaveButtonText}
              </button>
            )}

          </Modal.Footer>
        </CenteredModal>
        <ModalOverlay $show={show} />
      </>
    );
  };

  const getEditableFields = useCallback(() => {
    return columns
      .filter(col => (col.editable || col.onlyModal) && col.modalField)
      .map(col => ({
        ...col.modalField,
        nome: col.value,
        label: col.modalField?.label || col.label,
        apenasLeitura: col.modalField?.apenasLeitura,
        linha: col.modalField?.linha || 0,
        coluna: col.modalField?.coluna || 0,
        largura: col.modalField?.largura || 12,
        required: col.modalField?.required || false,
        tipo: col.modalField?.tipo || 'text'
      }));
  }, [columns, modo]);

  const labelFields = optionLabelField.includes(";")
    ? optionLabelField.split(";")
    : [optionLabelField];

  const createGroupedOptions = useCallback(() => {
    if (!groupByField) return null;

    const groups: Record<string, any[]> = {};

    dataSource.forEach(item => {
      const groupKey = item[groupByField] || 'Outros';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return Object.keys(groups).map(groupName => ({
      label: groupName,
      options: groups[groupName].map(item => ({
        label: labelFields.map(field => item[field] || "0").join(" - "),
        value: item,
      }))
    }));
  }, [dataSource, groupByField, labelFields]);

  const options = React.useMemo(
    () =>
      dataSource.map((item: any) => ({
        label: labelFields.map((field) => item[field] || "0").join(" - "),
        value: item,
      })),
    [dataSource, labelFields]
  );

  const selectOptions = groupByField
    ? createGroupedOptions()
    : options;

  useEffect(() => {
    if (setData && forceUpdate) {
      const formattedData = setData.map((item) => ({
        ...item,
        selected: false,
        id: item.id,
      }));
      const sortedData = sortData(formattedData, orderByField);
      setGridData(sortedData);
      onDataSourceChange(sortedData);
      setTableKey((prev) => prev + 1);
    }
  }, [setData, forceUpdate, onDataSourceChange, orderByField]);

  const CustomMenuList: React.FC<any> = (props) => {
    const { options, selectOption, gridColumns, inputValue } = props;
    const searchQuery = inputValue || "";
    const filteredOptions = filterOptionsByGridColumns(options, gridColumns, searchQuery);

    const isOptionInGrid = (option: any) => {
      return gridData.some((item: any) => isItemEqual(item, option.value));
    };

    return (
      <GridOptionsContainer>
        {filteredOptions.map((option: any) => {
          const included = isOptionInGrid(option);
          return (
            <GridOptionItem
              key={`${option.value.id}-${diffField ? option.value[diffField] : ''}`}
              onClick={() => !included && selectOption(option)}
              style={included ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
              $included={included}
            >
              {gridColumns.map((col: GridColumn) => (
                <GridOptionCell key={col.field}>
                  {col.renderCell
                    ? col.renderCell(option.value[col.field], option.value)
                    : highlightText(
                      option.value[col.field]?.toString() ?? col.defaultValue ?? "",
                      searchQuery
                    )}
                </GridOptionCell>
              ))}
              {included && <IncludedTag>Incluída</IncludedTag>}
            </GridOptionItem>
          );
        })}
      </GridOptionsContainer>
    );
  };

  const handleOptionSelect = useCallback((option: any) => {
    if (!option) return;

    setSelectedOption(option);

    const existingItem = gridData.find(item => isItemEqual(item, option.value));

    if (existingItem) {
      setEditingItem(existingItem);
      setShowEditModal(true);
      setSelectedOption(null);
      return;
    }

    const hasEditableFields = columns.some(col => (col.editable || col.onlyModal) && col.modalField);

    if (hasEditableFields) {
      const newItem = columns.reduce(
        (acc, col) => ({
          ...acc,
          [col.value]: option.value[col.value] ?? "",
        }),
        {
          id: option.value.id,
          selected: false
        }
      );
      setEditingItem(newItem);
      setShowEditModal(true);
      setSelectedOption(null);
    } else {
      addItemToGrid({
        ...option.value,
        id: option.value.id,
        selected: false
      });
      setSelectedOption(null);
    }
  }, [columns, addItemToGrid, gridData, isItemEqual]);

  const handleSaveEditModal = useCallback((entidade: any) => {
    const isNewItem = !gridData.some(item => isItemEqual(item, entidade));

    const newItem = {
      ...entidade,
      selected: false,
      id: entidade.id
    };

    const updatedData = isNewItem
      ? [...gridData, newItem]
      : gridData.map(item => isItemEqual(item, newItem) ? newItem : item);

    const sortedData = sortData(updatedData, orderByField);
    setGridData(sortedData);
    onDataSourceChange(sortedData);
    setShowEditModal(false);
    setSelectedOption(null);
    setTableKey(prev => prev + 1);
  }, [gridData, onDataSourceChange, orderByField, isItemEqual]);

  const handleRemove = useCallback(() => {
    const updatedData = gridData.filter((item) => !item.selected);
    setGridData(updatedData);
    onDataSourceChange(updatedData);
    setTableKey((prev) => prev + 1);
  }, [gridData, onDataSourceChange]);

  const handleRemoveSingle = useCallback((id: string) => {
    const updatedData = gridData.filter((item) => item.id !== id);
    setGridData(updatedData);
    onDataSourceChange(updatedData);
    setTableKey((prev) => prev + 1);
  }, [gridData, onDataSourceChange]);

  const handleEditSingle = useCallback((id: string) => {
    const itemToEdit = gridData.find(item => item.id === id);
    if (itemToEdit) {
      setEditingItem(itemToEdit);
      setShowEditModal(true);
    }
  }, [gridData]);

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      const updatedData = gridData.map((item) => ({
        ...item,
        selected: checked,
      }));
      setGridData(updatedData);
      onDataSourceChange(updatedData);
    },
    [gridData, onDataSourceChange]
  );

  const handleCheckboxChange = useCallback(
    (index: number) => {
      const updatedData = gridData.map((item, idx) =>
        idx === index ? { ...item, selected: !item.selected } : item
      );
      setGridData(updatedData);
      onDataSourceChange(updatedData);
    },
    [gridData, onDataSourceChange]
  );

  const isViewMode = modo === TipoFormulario.tpVisualizacao;
  const hasEditableFields = columns.some(col => (col.editable || col.onlyModal) && col.modalField);

  const handleInputChange = (newValue: string) => {
    setSearchInput(newValue);
  };

  const renderCellValue = (col: Column, value: any, row: any) => {
    if (col.renderCell) {
      return col.renderCell(value, row);
    }

    if (col.value.toLowerCase().includes('valor') || col.value.toLowerCase().includes('preço')) {
      return <MoneyValue>{formatarParaReais(value)}</MoneyValue>;
    }

    const displayValue = value?.toString() || '';
    return (
      <>
        <span className="truncated-text">{displayValue}</span>
        {displayValue.length > 20 && (
          <TooltipText className="tooltip-text">{displayValue}</TooltipText>
        )}
      </>
    );
  };

  return (
    <>
      <SelectGridContainer style={style} height={height}>
        <SelectGridHeader>
          <SelectGridSelect
            classNamePrefix="react-select"
            onChange={handleOptionSelect}
            options={selectOptions || []}
            placeholder="Clique para selecionar uma opção"
            data-tour="selectgrid-inserir"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
              menu: (base: any) => ({ ...base, zIndex: 9999 }),
            }}
            components={{
              Input: ReactSelectComponents.Input,
              ...(useGridOptions && gridColumns.length > 0
                ? {
                  MenuList: (props: any) => (
                    <CustomMenuList
                      {...props}
                      gridColumns={gridColumns}
                      inputValue={searchInput}
                    />
                  ),
                }
                : {}),
            }}
            getOptionLabel={(e: any) => e.label}
            getOptionValue={(e: any) => e.label}
            noOptionsMessage={() => "Nenhuma opção encontrada"}
            isDisabled={isViewMode}
            value={selectedOption}
            isSearchable={true}
            onInputChange={handleInputChange}
            formatGroupLabel={formatGroupLabel || defaultFormatGroupLabel}
          />
          <SelectGridButtons>
            <SelectGridButton
              type="button"
              onClick={handleRemove}
              title="Remover selecionados"
              disabled={isViewMode || !gridData.some((item) => item.selected)}
              variant="remove"
            >
              <FaTrash />
            </SelectGridButton>
            {onAcaoExtra && tituloBotaoExtra && iconeBotaoExtra && (
              <SelectGridButton
                type="button"
                onClick={onAcaoExtra}
                title={tituloBotaoExtra}
                disabled={isViewMode}
                variant="add"
                data-tour="selectgrid-btn-novo"
              >
                {React.createElement(iconeBotaoExtra)}
              </SelectGridButton>
            )}
          </SelectGridButtons>
        </SelectGridHeader>
        <SelectGridTableContainer ref={tableContainerRef}>
          {gridData.length > 0 ? (
            <TableWrapper>
              <SelectGridTable key={tableKey}>
                <thead>
                  <TableHeaderRow>
                    <TableHeader>
                      <input
                        type="checkbox"
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        checked={
                          gridData.length > 0 &&
                          gridData.every((item) => item.selected)
                        }
                        disabled={isViewMode}
                      />
                    </TableHeader>
                    {gridVisibleColumns.map((col) => ( // Usa gridVisibleColumns em vez de columns
                      <TableHeader key={col.value}>{col.label}</TableHeader>
                    ))}
                    <TableHeader style={{ width: '100px' }}></TableHeader>
                  </TableHeaderRow>
                </thead>
                <tbody>
                  {gridData.map((row, index) => (
                    <tr key={`${row.id}-${diffField ? row[diffField] : ''}`}>
                      <CheckboxCell>
                        <input
                          type="checkbox"
                          checked={row.selected || false}
                          onChange={() => handleCheckboxChange(index)}
                          disabled={isViewMode}
                        />
                      </CheckboxCell>
                      {gridVisibleColumns.map((col) => ( // Usa gridVisibleColumns em vez de columns
                        <TableCell key={col.value}>
                          {renderCellValue(col, row[col.value], row)}
                        </TableCell>
                      ))}
                      <ActionCell>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          {hasEditableFields && (
                            <EditButton
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleEditSingle(row.id);
                              }}
                              disabled={isViewMode}
                              title="Editar item"
                            >
                              <FaEdit />
                            </EditButton>
                          )}
                          <DeleteButton
                            onClick={() => handleRemoveSingle(row.id)}
                            disabled={isViewMode}
                            title="Remover item"
                          >
                            <FaTrash />
                          </DeleteButton>
                        </div>
                      </ActionCell>
                    </tr>
                  ))}
                </tbody>
              </SelectGridTable>
            </TableWrapper>
          ) : (
            <NoRecordsWrapper>
              <NoRecords>Nenhum registro inserido</NoRecords>
            </NoRecordsWrapper>
          )}
        </SelectGridTableContainer>
      </SelectGridContainer>

      <EditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        item={editingItem || {}}
        onSave={handleSaveEditModal}
        campos={getEditableFields()}
      />
    </>
  );
};

export default SelectGrid;